/* ==========================================================================
   GAUI — header.js
   Mobile drawer (open/close, focus trap, scroll lock, Escape, overlay) and
   desktop mega-menu accessibility (aria-expanded sync + Escape-to-close).
   The mega panel itself opens via CSS :hover / :focus-within, so it works with
   JS disabled; this script layers in the assistive-tech affordances.
   ========================================================================== */
(function () {
  "use strict";

  /* Theme editor: changing any Header setting re-renders this section's DOM,
     which destroys every element-bound listener below. Without re-running,
     the hamburger, the Shop dropdown and the collection rail all go dead
     until a full reload — exactly the "glitches" reported while editing the
     menus (client 2026-07-27). initHeader() re-binds on shopify:section:load;
     document-level listeners are registered once behind docBound. */
  var docBound = false;

  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function isVisible(el) {
    return el.offsetWidth > 0 || el.offsetHeight > 0 || el.getClientRects().length > 0;
  }

  function initHeader() {
  /* ----------------------------------------------------- Mobile drawer ---- */
  var drawer = document.querySelector("[data-drawer]");
  var toggle = document.querySelector("[data-menu-toggle]");

  if (drawer && toggle) {
    var panel = drawer.querySelector("[data-drawer-panel]");
    var lastFocused = null;

    var onKeydown = function (e) {
      if (e.key === "Escape") {
        closeDrawer();
        return;
      }
      if (e.key !== "Tab" || !panel) return;
      var focusables = Array.prototype.slice
        .call(panel.querySelectorAll(FOCUSABLE))
        .filter(isVisible);
      if (!focusables.length) return;
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    var openDrawer = function () {
      lastFocused = document.activeElement;
      drawer.classList.add("is-open");
      document.body.classList.add("drawer-open");
      toggle.setAttribute("aria-expanded", "true");
      toggle.classList.add("is-open");
      var closeBtn = drawer.querySelector("[data-drawer-close]");
      window.requestAnimationFrame(function () {
        (closeBtn || panel).focus();
      });
      document.addEventListener("keydown", onKeydown);
    };

    var closeDrawer = function () {
      drawer.classList.remove("is-open");
      document.body.classList.remove("drawer-open");
      toggle.setAttribute("aria-expanded", "false");
      toggle.classList.remove("is-open");
      document.removeEventListener("keydown", onKeydown);
      if (lastFocused && typeof lastFocused.focus === "function") lastFocused.focus();
    };

    toggle.addEventListener("click", openDrawer);
    drawer.addEventListener("click", function (e) {
      if (e.target.closest("[data-drawer-close]") || e.target.hasAttribute("data-drawer-overlay")) {
        closeDrawer();
      }
    });
  }

  /* -------------------------------------------------- Mega-menu a11y ------ */
  var headerEl = document.querySelector("[data-header]");
  var trigger = document.querySelector("[data-shop-trigger]");
  var mega = document.querySelector("[data-mega]");

  if (headerEl && trigger && mega) {
    var openMega = function () {
      mega.hidden = false;
      headerEl.classList.add("is-shop-open");
      trigger.setAttribute("aria-expanded", "true");
    };
    var closeMega = function () {
      mega.hidden = true;
      headerEl.classList.remove("is-shop-open");
      trigger.setAttribute("aria-expanded", "false");
    };

    // pointerenter (mouse only) — on touch, hover and click fire together
    // and the naive toggle closed the panel the instant it opened.
    trigger.addEventListener("pointerenter", function (e) {
      if (e.pointerType === "mouse") openMega();
    });
    trigger.addEventListener("click", function () {
      mega.hidden ? openMega() : closeMega();
    });
    // Close when the pointer leaves the whole header (bar + mega), or on Escape.
    headerEl.addEventListener("mouseleave", closeMega);
    if (!docBound) {
      document.addEventListener("keydown", function (e) {
        var m = document.querySelector("[data-mega]");
        var t = document.querySelector("[data-shop-trigger]");
        var h = document.querySelector("[data-header]");
        if (e.key === "Escape" && m && !m.hidden) {
          m.hidden = true;
          if (h) h.classList.remove("is-shop-open");
          if (t) { t.setAttribute("aria-expanded", "false"); t.focus(); }
        }
      });
    }

    // Left-rail collection switcher: hover swaps the preview panel.
    var railBtns = mega.querySelectorAll("[data-mega-cat]");
    var panels = mega.querySelectorAll("[data-mega-panel]");
    Array.prototype.forEach.call(railBtns, function (btn) {
      var activate = function () {
        var idx = btn.getAttribute("data-mega-cat");
        Array.prototype.forEach.call(railBtns, function (b) {
          b.classList.toggle("is-active", b === btn);
        });
        Array.prototype.forEach.call(panels, function (p) {
          p.hidden = p.getAttribute("data-mega-panel") !== idx;
        });
      };
      btn.addEventListener("mouseenter", activate);
      btn.addEventListener("focus", activate);
      btn.addEventListener("click", activate);
    });

    // Warm the hidden panels' photos once the browser is idle (same pattern
    // as the mobile menu) so rail switches never flash a bare tile.
    var megaPreload = function () {
      mega.querySelectorAll('[data-mega-panel] img[loading="lazy"], .mega__featured img[loading="lazy"]').forEach(function (img) {
        img.setAttribute("loading", "eager");
      });
    };
    // desktop-only: below 990px the mega is display:none — eager-flipping
    // its ~25 images would burn phone bandwidth on every page load.
    if (window.matchMedia("(min-width: 990px)").matches) {
      if ("requestIdleCallback" in window) { requestIdleCallback(megaPreload, { timeout: 4000 }); }
      else { setTimeout(megaPreload, 2500); }
    }

    // Mega search field -> submit to /search on Enter.
    var megaSearch = mega.querySelector("[data-search-open]");
    if (megaSearch) {
      megaSearch.addEventListener("keydown", function (e) {
        if (e.key === "Enter" && megaSearch.value.trim()) {
          var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
          window.location.href = root + "search?q=" + encodeURIComponent(megaSearch.value.trim());
        }
      });
    }
  }

  /* ------------------------------------ Overlay header solidify on scroll --
     On the home phone layout the header floats transparently over the hero
     (see header.liquid). Once the page scrolls off the top, .is-scrolled swaps
     it to the solid bar. CSS scopes the effect to body.template-index + phone,
     so toggling the class elsewhere is a harmless no-op. */
  var overlayHeader = document.querySelector("[data-header]");
  if (overlayHeader) {
    var syncOverlay = function () {
      var y = window.pageYOffset || document.documentElement.scrollTop || 0;
      overlayHeader.classList.toggle("is-scrolled", y > 40);
    };
    syncOverlay();
    if (!docBound) {
      window.addEventListener("scroll", function () {
        var h = document.querySelector("[data-header]");
        if (!h) return;
        var y = window.pageYOffset || document.documentElement.scrollTop || 0;
        h.classList.toggle("is-scrolled", y > 40);
      }, { passive: true });
    }
  }

  /* --------------------------------------- "Shop the Collection" sweep --
     The menu CTA plays a short cream sweep (colors invert, label flips to
     ink) before routing to the active tab's collection. Reduced-motion
     visitors navigate immediately; pageshow clears the state when the
     visitor comes back via the back button (bfcache restore). */
  var sweepCta = document.querySelector("[data-mmenu-cta]");
  if (sweepCta) {
    sweepCta.addEventListener("click", function (e) {
      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
      if (sweepCta.classList.contains("is-sweeping")) return;
      e.preventDefault();
      sweepCta.classList.add("is-sweeping");
      var href = sweepCta.getAttribute("href");
      window.setTimeout(function () { window.location.href = href; }, 380);
    });
    if (!docBound) {
      window.addEventListener("pageshow", function () {
        document.querySelectorAll(".is-sweeping").forEach(function (el) { el.classList.remove("is-sweeping"); });
      });
    }
  }

  docBound = true;
  }

  /* Desktop mega-menu category groups: a nav item with nested links renders
     as a disclosure so a long group cannot run the column past the viewport
     (client 2026-07-27). Delegated, so it survives theme-editor re-renders
     and is registered exactly once. */
  document.addEventListener("click", function (e) {
    var acc = e.target.closest ? e.target.closest("[data-mega-acc]") : null;
    if (!acc) return;
    e.preventDefault();
    var sub = acc.nextElementSibling;
    if (!sub || !sub.classList.contains("mega__col-sub")) return;
    var open = sub.hidden;
    sub.hidden = !open;
    acc.setAttribute("aria-expanded", open ? "true" : "false");
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initHeader);
  } else {
    initHeader();
  }
  // Re-bind after the theme editor swaps this section's markup.
  document.addEventListener("shopify:section:load", function (e) {
    if (e.target && e.target.querySelector && e.target.querySelector("[data-header]")) initHeader();
  });
})();
